import BigNumber from 'bignumber.js';
import { computed, ref } from 'vue';
import { Token } from '@/sdk/entities/token';
import { Portfolio, TokenInfo } from '@/sdk/entities/portfolio';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import { fromWei } from '@/sdk/utils';
import { useFarmingTokens } from '@/store/modules/tokens/useFarmingTokens';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { BIG_ONE, BIG_ZERO, max } from '@/utils/bigNumber';
import { SingleSideToken } from '../models/single-side-token';
import { PortfolioSource } from '@/sdk/entities/PortfolioSource';

const LOGGER = {
  groupCollapsed: (...label: any[]) => {
    if (isLoggingDisabled()) return;

    console.groupCollapsed(...label);
  },
  groupEnd: () => {
    if (isLoggingDisabled()) return;

    console.groupEnd();
  },
  log: (message?: any, ...optionalParams: any[]) => {
    if (isLoggingDisabled()) return;

    console.log(message, ...optionalParams);
  },
  table: (tabularData: any, properties?: readonly string[] | undefined) => {
    if (isLoggingDisabled()) return;

    console.table(tabularData, properties);
  },
};

export function useSingleSideDepositTokens() {
  const farmingTokensStore = useFarmingTokens();
  const { getBalanceByToken } = useBalances();

  const farmsBySingleSideToken = ref<Record<string, PortfolioFarm> | {}>({});

  const getTokenBalance = (token: Token): string | undefined => {
    return getBalanceByToken(token)?.balance.toFixed();
  };

  const tokens = computed<SingleSideToken[]>(() => {
    farmsBySingleSideToken.value = {};

    return farmingTokensStore.tokens.map<SingleSideToken>(farmingToken => {
      LOGGER.groupCollapsed(
        `[SINGLE SIDE DEPOSIT] INIT | `,
        `PORTFOLIO : ${farmingToken.farm.portfolio.name} | `,
        `TOKEN : ${farmingToken.tokenInfo.token.unwrapWETH().symbol}`,
      );
      const tokenInfo = farmingToken.tokenInfo as TokenInfo;
      const token: Token = tokenInfo.token.unwrapWETH();
      const singleSideTokenId = `${farmingToken.farm.farm}-${token.address}`;
      const portfolio = farmingToken.farm.portfolio as Portfolio;
      const tokenBalance = getTokenBalance(token!);
      const balanceLPInWallet = BigNumber(farmingToken.farm.portfolio.balanceOfWallet);
      const balanceLPInFarm = BigNumber(farmingToken.farm.deposited);
      const noFeeLimitInTokenWei = calculateDepositNoFeeLimit(
        portfolio.totalValueWithProtocolFee,
        tokenInfo,
      );
      // BASE WEI / TOKEN WEI
      const tokenPriceBaseInWei = tokenInfo.price.shiftedBy(
        portfolio.baseToken.decimals - token.decimals,
      );
      const noFeeLimitInUSD = fromWei(
        noFeeLimitInTokenWei.multipliedBy(tokenPriceBaseInWei),
        portfolio.baseToken.decimals,
      ).multipliedBy(portfolio.priceInUSD);

      farmsBySingleSideToken.value[singleSideTokenId] = farmingToken.farm;
      LOGGER.groupEnd();

      return {
        id: singleSideTokenId,
        portfolioToken: token,
        isCrossChain: portfolio.type === PortfolioSource.PORTFOLIO_CROSSCHAIN,
        balance: tokenBalance ? BigNumber(tokenBalance) : BIG_ZERO,
        balanceLPInWallet,
        balanceLPInFarm,
        targetWeight: tokenInfo.targetWeight,
        apr: BigNumber(farmingToken.farm.printIPR),
        noFeeLimit: BigNumber(
          fromWei(noFeeLimitInTokenWei, token.decimals).toFixed(
            token.decimals,
            BigNumber.ROUND_DOWN,
          ),
        ),
        noFeeLimitInUSD,
        portfolio: {
          name: portfolio.name,
          address: portfolio.contractAddress,
        },
      };
    });
  });

  const isLoadingTokens = computed<boolean>(() => {
    return !farmingTokensStore.isStoreReady;
  });

  return {
    isLoadingTokens,
    tokens,
    farmsBySingleSideToken,
  };
}

/**
 * returns deposit no fee limit in token wei units
 */
function calculateDepositNoFeeLimit(portfolioValueWei: BigNumber, tokenInfo: TokenInfo): BigNumber {
  // BASE WEI / TOKEN WEI
  const tokenPriceBaseInWei = tokenInfo
    .getPriceRelative()
    .shiftedBy(tokenInfo.baseToken.decimals - tokenInfo.token.decimals);

  // V0 * Wi - Ri * POi
  const lackOfTokenInBaseWei = portfolioValueWei
    .multipliedBy(tokenInfo.targetWeight)
    .minus(tokenInfo.baseTokenAmountEquivalent);

  // ( V0 * Wi - Ri * POi ) / ( 1 - Wi ) / POi
  const maxNoFeeLimitInTokenWei = lackOfTokenInBaseWei
    .div(BIG_ONE.minus(tokenInfo.targetWeight))
    .div(tokenPriceBaseInWei);
  // max( ( V0 * Wi - Ri * POi ) / ( 1 - Wi ) / POi , 0 )
  const noFeeLimit = max(maxNoFeeLimitInTokenWei, 0);

  LOGGER.groupCollapsed(
    `[SINGLE SIDE DEPOSIT] NO_FEE_LIMIT : ${tokenInfo.token.unwrapWETH().symbol} : `,
    noFeeLimit.shiftedBy(-tokenInfo.token.decimals).toString(),
  );
  LOGGER.log('( V0 * Wi - Ri * POi ) / ( 1 - Wi ) / POi');
  LOGGER.table({
    'V0 [ BASE WEI ]': portfolioValueWei.toString(),
    'Wi [ SHARE ]': tokenInfo.targetWeight.toString(),
    'Ri * POi [ BASE WEI ]': tokenInfo.baseTokenAmountEquivalent.toString(),
    'POi [ BASE WEI / TOKEN WEI ]': tokenPriceBaseInWei.toString(),
    'CALC NO FEE LIMIT [ TOKEN WEI ]': maxNoFeeLimitInTokenWei.toFixed(),
    ' ': ' ',
    'V0 [ BASE ]': portfolioValueWei.shiftedBy(-tokenInfo.baseToken.decimals).toString(),
    'Wi [ % ]': tokenInfo.targetWeight.multipliedBy(100).toString(),
    'Ri * POi [ BASE ]': tokenInfo.baseTokenAmountEquivalent
      .shiftedBy(-tokenInfo.baseToken.decimals)
      .toString(),
    'POi [ BASE / TOKEN ]': tokenInfo.getPriceRelative().toString(),
    'CALC NO FEE LIMIT [ TOKEN ]': maxNoFeeLimitInTokenWei
      .shiftedBy(-tokenInfo.token.decimals)
      .toString(),
  });
  LOGGER.groupEnd();

  return noFeeLimit;
}

// DEBUG
function isLoggingDisabled() {
  return !window['BLUESHIFT_DEBUG'].SINGLE_SIDE_DEPOSIT;
}
